import React, { useState } from 'react';
import { useStore } from '../../../store/useStore';
import { Card } from '../../ui/Card';
import { Button } from '../../ui/Button';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MessageSquare,
  Edit2,
  CheckCircle,
  Clock
} from 'lucide-react';
import { format } from 'date-fns';
import { TaskEditModal } from '../../tasks/TaskEditModal';
import { CeoMetrics } from './CeoMetrics';
import { TaskOverview } from './TaskOverview';

export const CeoDashboard: React.FC = () => {
  const { currentUser, tasks } = useStore();
  const [showTaskForm, setShowTaskForm] = useState(false);
  
  // Get tasks assigned by CEO
  const assignedTasks = tasks.filter(task => task.assignedBy === currentUser?.id);
  
  // Get tasks assigned to CEO
  const receivedTasks = tasks.filter(task => task.assignedTo === currentUser?.id);

  return (
    <div className="max-w-7xl mx-auto space-y-8">
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-6">
            <img
              src={currentUser?.avatar}
              alt={currentUser?.name}
              className="w-16 h-16 rounded-full ring-4 ring-emerald-500 ring-offset-4"
            />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">CEO Dashboard</h1>
              <p className="text-gray-600">Welcome back, {currentUser?.name}</p>
            </div>
          </div>
          <Button
            variant="primary"
            size="lg"
            onClick={() => setShowTaskForm(true)}
            icon={<Edit2 className="w-5 h-5" />}
            className="shadow-lg hover:shadow-xl transition-shadow"
          >
            Assign Tasks
          </Button>
        </div>

        <CeoMetrics />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
          <TaskOverview />

          <Card className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-purple-100 rounded-lg">
                <MessageSquare className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">Tasks Assigned to You</h2>
                <p className="text-sm text-gray-600">Tasks from departments</p>
              </div>
            </div>

            <div className="space-y-4">
              <AnimatePresence>
                {receivedTasks.map((task) => (
                  <motion.div
                    key={task.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-medium text-gray-900">{task.title}</h3>
                        <p className="text-sm text-gray-600 mt-1">{task.description}</p>
                        <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                          <span>From: {task.assignedBy}</span>
                          <span>•</span>
                          <span>Due: {format(new Date(task.dueDate), 'MMM d, h:mm a')}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {!task.acknowledged && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {}}
                            icon={<Clock className="w-4 h-4" />}
                            className="text-blue-600 border-blue-200 hover:bg-blue-50"
                          >
                            Acknowledge
                          </Button>
                        )}
                        {task.acknowledged && task.status !== 'COMPLETED' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {}}
                            icon={<CheckCircle className="w-4 h-4" />}
                            className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                          >
                            Complete
                          </Button>
                        )}
                        {task.status === 'COMPLETED' && (
                          <span className="flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm">
                            <CheckCircle className="w-4 h-4" />
                            <span className="text-xs font-medium">Completed</span>
                          </span>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
              
              {receivedTasks.length === 0 && (
                <p className="text-center py-8 text-gray-500">
                  No tasks assigned to you
                </p>
              )}
            </div>
          </Card>
        </div>
      </div>

      <TaskEditModal 
        isOpen={showTaskForm}
        onClose={() => setShowTaskForm(false)}
      />
    </div>
  );
};